import { useState } from 'react'
import { Statistics } from '../api'
import { Sparkles, CheckCircle, Trash2, AlertCircle, Settings, ClipboardList, Folder, Moon, Sun, ArrowLeft } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'

interface CompletionScreenProps {
  stats: Statistics
  totalProducts: number
  onNewSession: () => void
  onViewHistory: () => void
  onClearAll: () => void
  onGoToDashboard?: () => void
  onBack?: () => void
}

export function CompletionScreen({
  stats,
  totalProducts,
  onNewSession,
  onViewHistory,
  onClearAll,
  onGoToDashboard,
  onBack
}: CompletionScreenProps) {
  const { theme, toggleTheme, logo } = useTheme()
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)

  return (
    <div className="min-h-screen bg-gradient-light dark:bg-gradient-dark transition-colors duration-300 flex items-center justify-center p-3 sm:p-4">
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 sm:top-6 right-4 sm:right-6 p-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
      >
        {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
      </button>

      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl p-6 sm:p-12 max-w-2xl w-full border border-gray-200 dark:border-slate-700">
        {/* Logo */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <img
            src={logo}
            alt="Seridóplast"
            className="h-12 sm:h-14 w-auto object-contain"
          />
        </div>

        {/* Ícone de celebração */}
        <div className="text-center mb-6 sm:mb-8">
          <Sparkles size={48} className="mx-auto mb-3 sm:mb-4 text-[#FCD016] dark:text-[#FCD016] sm:block hidden" />
          <Sparkles size={40} className="mx-auto mb-3 sm:mb-4 text-[#FCD016] dark:text-[#FCD016] sm:hidden" />
          <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] bg-clip-text text-transparent mb-2">
            Conferência Finalizada!
          </h1>
        </div>

        {/* Resumo */}
        <div className="bg-[#E5191F]/5 dark:bg-[#FCD016]/5 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 border border-[#E5191F]/20 dark:border-[#FCD016]/20">
          <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">Resumo Final</h2>

          <div className="grid grid-cols-2 gap-3 sm:gap-6">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">Total de Produtos</p>
              <p className="text-2xl sm:text-3xl font-bold text-[#E5191F] dark:text-[#FCD016]">{totalProducts}</p>
            </div>

            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">Aprovados</p>
              <p className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400">{stats.approved}</p>
            </div>

            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">Sem Imagem</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-600 dark:text-gray-400">{stats.noImage}</p>
            </div>

            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">Pulados</p>
              <p className="text-2xl sm:text-3xl font-bold text-orange-600 dark:text-orange-400">{stats.skipped}</p>
            </div>
          </div>

          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-[#E5191F]/20 dark:border-[#FCD016]/20">
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">Taxa de Conclusão</p>
            <p className="text-xl sm:text-2xl font-bold text-[#E5191F] dark:text-[#FCD016]">
              {Math.round((stats.approved / totalProducts) * 100)}%
            </p>
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 sm:h-3 mt-2">
              <div
                className="bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] h-2 sm:h-3 rounded-full transition-all"
                style={{ width: `${(stats.approved / totalProducts) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Botões de ação */}
        <div className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
          <button
            onClick={onNewSession}
            className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-bold py-2 sm:py-3 rounded-lg transition flex items-center justify-center gap-2 text-xs sm:text-base"
          >
            <CheckCircle size={14} className="sm:block hidden flex-shrink-0" />
            <CheckCircle size={12} className="sm:hidden flex-shrink-0" />
            Iniciar Nova Conferência
          </button>

          {onGoToDashboard && (
            <button
              onClick={onGoToDashboard}
              className="w-full bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] hover:from-[#C91419] hover:to-[#E01A36] dark:hover:from-[#C9A00A] dark:hover:to-[#F0BA00] text-white dark:text-[#18181A] font-bold py-2 sm:py-3 rounded-lg transition flex items-center justify-center gap-2 text-xs sm:text-base"
            >
              <Settings size={14} className="sm:block hidden flex-shrink-0" />
              <Settings size={12} className="sm:hidden flex-shrink-0" />
              <span>Dashboard</span>
            </button>
          )}

          <button
            onClick={onViewHistory}
            className="w-full bg-gray-600 hover:bg-gray-700 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-bold py-2 sm:py-3 rounded-lg transition flex items-center justify-center gap-2 text-xs sm:text-base"
          >
            <ClipboardList size={14} className="sm:block hidden flex-shrink-0" />
            <ClipboardList size={12} className="sm:hidden flex-shrink-0" />
            Ver Histórico
          </button>

          {!showConfirmDelete ? (
            <button
              onClick={() => setShowConfirmDelete(true)}
              className="w-full bg-[#C91419] hover:bg-[#E5191F] dark:bg-[#E5191F] dark:hover:bg-[#F03B40] text-white font-bold py-2 sm:py-3 rounded-lg transition flex items-center justify-center gap-2 text-xs sm:text-base"
            >
              <Trash2 size={14} className="sm:block hidden flex-shrink-0" />
              <Trash2 size={12} className="sm:hidden flex-shrink-0" />
              Limpar Tudo
            </button>
          ) : (
            <div className="bg-[#E5191F]/10 dark:bg-[#E5191F]/10 border-2 border-[#E5191F] dark:border-[#FCD016] rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-[#C91419] dark:text-[#FFE066] font-semibold mb-2 sm:mb-3 flex items-center gap-2">
                <AlertCircle size={14} className="sm:block hidden flex-shrink-0" />
                <AlertCircle size={12} className="sm:hidden flex-shrink-0" />
                Tem certeza? Isso vai deletar todos os dados!
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setShowConfirmDelete(false)}
                  className="flex-1 bg-gray-400 hover:bg-gray-500 text-white font-bold py-1.5 sm:py-2 rounded transition text-xs sm:text-base"
                >
                  Cancelar
                </button>
                <button
                  onClick={onClearAll}
                  className="flex-1 bg-[#C91419] hover:bg-[#E5191F] text-white font-bold py-1.5 sm:py-2 rounded transition text-xs sm:text-base"
                >
                  Sim, Deletar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Informações */}
        <div className="text-center text-xs sm:text-sm text-gray-600 dark:text-gray-300 space-y-1 sm:space-y-2">
          <p className="flex items-center justify-center gap-2">
            <CheckCircle size={12} className="text-green-600 dark:text-green-400 flex-shrink-0 sm:block hidden" />
            <CheckCircle size={10} className="text-green-600 dark:text-green-400 flex-shrink-0 sm:hidden" />
            Seus dados foram salvos em segurança.
          </p>
          <p className="flex items-center justify-center gap-2">
            <Folder size={12} className="text-[#E5191F] dark:text-[#FCD016] flex-shrink-0 sm:block hidden" />
            <Folder size={10} className="text-[#E5191F] dark:text-[#FCD016] flex-shrink-0 sm:hidden" />
            Histórico disponível para consulta.
          </p>
        </div>
      </div>
    </div>
  )
}
